import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AssessmentComparison, ReferenceKeyList } from './AssessmentComparison';
import AssessmentAudioCard from './AssessmentAudioCard';
import { centreIn, useAssessmentPlayer } from './useAssessmentPlayer';
import {
  buildComparison, comparisonCounts, referenceKeyDoc, referenceKeySections, segmentAt,
} from '../../../models/assessmentEvaluation';

const segmentIn = (scroller, index) => scroller && scroller.querySelector(`[data-segment="${index}"]`);

/**
 * The evaluation loop's two columns, each scrolling its own list in place:
 * the comparison on the left, the recording and its transcript on the right.
 * Processing shows the key with no outcomes beside the player (which plays)
 * and a waiting transcript; Completed shows the comparison.
 */
function AssessmentEvaluation({ assessment, scenario, documents }) {
  const processing = assessment.status !== 'completed';
  const fromTranscript = assessment.source === 'transcript';
  const player = useAssessmentPlayer();
  const transcriptRef = useRef(null);
  const [markedIndex, setMarkedIndex] = useState(null);
  const activeIndex = segmentAt(player.time);

  const groups = useMemo(() => buildComparison(scenario, documents), [scenario, documents]);
  const counts = useMemo(() => comparisonCounts(groups), [groups]);

  // The transcript follows the clip only while it plays, so reading ahead is
  // not yanked back.
  useEffect(() => {
    if (player.playing) centreIn(transcriptRef.current, segmentIn(transcriptRef.current, activeIndex));
  }, [activeIndex, player.playing]);

  // A transcript row and a card's timestamp both land here: the clip seeks
  // and the segment it belongs to comes into view.
  const seekTo = useCallback((sec) => {
    player.seek(sec);
    centreIn(transcriptRef.current, segmentIn(transcriptRef.current, segmentAt(sec)));
  }, [player]);

  // Built on a transcript a card marks its passage instead of playing from it.
  // The mark stays until another is asked for, and focus follows the jump.
  const showPassage = useCallback((index) => {
    setMarkedIndex(index);
    const passage = segmentIn(transcriptRef.current, index);
    centreIn(transcriptRef.current, passage);
    if (passage) passage.focus({ preventScroll: true });
  }, []);

  return (
    <div className="assessment-eval-split">
      <div className="assessment-eval-pane">
        {processing
          ? <ReferenceKeyList sections={referenceKeySections(scenario, documents)} keyName={referenceKeyDoc(scenario, documents)?.name} />
          : (
            <AssessmentComparison
              groups={groups}
              counts={counts}
              fromTranscript={fromTranscript}
              onSeek={seekTo}
              onShowPassage={showPassage}
            />
          )}
      </div>
      <div className="assessment-eval-pane">
        <AssessmentAudioCard
          fileName={assessment.fileName}
          fromTranscript={fromTranscript}
          processing={processing}
          player={player}
          activeIndex={activeIndex}
          markedIndex={markedIndex}
          transcriptRef={transcriptRef}
          onSeek={seekTo}
        />
      </div>
    </div>
  );
}

export default AssessmentEvaluation;
